import { useState, useCallback } from 'react';
import { apiService, CreateBranchRequest, UpdateBranchRequest } from '../services/api';
import type { BranchDto, BusinessBranchListResponse } from '../types/api-responses';
import { getApiErrorMessage } from '../types/api-errors';

export const useBranches = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const getBranches = useCallback(async (): Promise<BusinessBranchListResponse> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.getBranches();
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to fetch branches');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    const getBranchById = useCallback(async (id: number): Promise<BranchDto> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.getBranchById(id);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to fetch branch');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    const createBranch = useCallback(async (data: CreateBranchRequest): Promise<number> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.createBranch(data);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to create branch');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    const updateBranch = useCallback(async (id: number, data: UpdateBranchRequest): Promise<boolean> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.updateBranch(id, data);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to update branch');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    const deleteBranch = useCallback(async (id: number): Promise<boolean> => {
        setLoading(true);
        setError(null);
        try {
            return await apiService.deleteBranch(id);
        } catch (err: unknown) {
            const msg = getApiErrorMessage(err, 'Failed to delete branch');
            setError(msg);
            throw new Error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        getBranches,
        getBranchById,
        createBranch,
        updateBranch,
        deleteBranch,
        loading,
        error
    };
};
